import { z } from "zod";
import {
  type PrescriptionCycleState,
  prescriptionCycleStateSchema,
} from "./prescription-cycle";

// TES-749 — the recurring monthly cycle, presented twice.
//
// The coach-admin package describes Aktiv behandling as a long phase with a
// cycle repeating inside it:
//
//   ongoing treatment → monthly check-in → medical evidence
//   → doctor's assessment → prescription renewal/dose decision
//   → new treatment month
//
// That is not a new state machine. It is the SAME `PrescriptionCycle` states
// that already exist, read as a loop. This file is the one place that mapping
// lives, for the same reason `prescriptionLabel` lives in contracts: coach
// admin, doctor admin and the patient app must agree on how a cycle READS, and
// three copies of that judgement drift within a sprint.
//
// Two derived values, and they are derived on purpose — never columns. The
// implementation contract is explicit that `journey_state` is the single
// source of truth and that treatment phase and cycle day are computed, never
// stored in parallel; `Patient.journeyState` carries the same note in the
// schema. Storing `treatmentMonth` would create exactly the second truth that
// rule exists to prevent.

// ---------------------------------------------------------------------------
// The cycle step — what the coach sees
// ---------------------------------------------------------------------------

// Deliberately NOT the raw cycle state. `doctor_review` and `renewal_pending`
// are clinically meaningful distinctions; "väntar på läkarbedömning" is what a
// coach needs. The two extra steps are the states that sit outside the loop.
export const CYCLE_STEPS = [
  // Not in the loop yet — the first cycle is waiting on activation.
  "awaiting_activation",
  "ongoing_treatment",
  "monthly_checkin",
  "medical_evidence",
  "doctor_review",
  "renewal_decision",
  "paused",
  "ended",
] as const;
export const cycleStepSchema = z.enum(CYCLE_STEPS);
export type CycleStep = z.infer<typeof cycleStepSchema>;

// The only mapping from cycle state to the loop. Exhaustive by Record type, so
// a new cycle state is a compile error here rather than a silent fall-through
// to "ongoing_treatment" — which would tell a coach that everything is fine
// during a state nobody has thought about yet.
export const CYCLE_STEP_BY_STATE: Record<PrescriptionCycleState, CycleStep> = {
  pending_assessment: "awaiting_activation",
  active: "ongoing_treatment",
  // The renewal CTA is gated on `expiring_soon`, so this IS the month's
  // check-in becoming due — not a warning about anything.
  expiring_soon: "monthly_checkin",
  renewal_pending: "medical_evidence",
  doctor_review: "doctor_review",
  // Terminal, and it spawns the next cycle — the loop's closing step.
  approved: "renewal_decision",
  rejected: "ended",
  paused: "paused",
  discontinued: "ended",
};

export function cycleStepFor(state: PrescriptionCycleState): CycleStep {
  return CYCLE_STEP_BY_STATE[state];
}

// Whole days from `now` until the prescription runs out. Null when the cycle
// carries no expiry (a cycle still awaiting activation has none).
//
// Negative is a real answer, not an error: an expired prescription the patient
// is still inside is precisely the situation both roles need to see. Callers
// that want "expired" should test `<= 0`, not treat null and negative alike.
export function daysUntilRenewal(
  expiresAt: Date | string | null,
  now: Date = new Date(),
): number | null {
  if (!expiresAt) return null;
  const end = typeof expiresAt === "string" ? new Date(expiresAt) : expiresAt;
  const ms = end.getTime() - now.getTime();
  return Math.ceil(ms / 86_400_000);
}

// ---------------------------------------------------------------------------
// The two views
// ---------------------------------------------------------------------------

// What the coach sees. Neutral by construction: there is no field here that
// could carry a drug name, a dose, a lab value or clinical free text, so the
// boundary is enforced by the type rather than by remembering to omit things.
// The coach patient page already says out loud that
// "recept, provsvar och journaltext ligger hos läkaren" — this keeps that
// true.
export const coachCycleViewSchema = z.object({
  cycleId: z.string(),
  // = cycleNumber. The patient's Nth month of treatment.
  treatmentMonth: z.number().int().positive(),
  step: cycleStepSchema,
  daysUntilRenewal: z.number().int().nullable(),
  // Whether the patient has submitted this month's check-in. A coach needs to
  // know whether she has done her part, not what she answered.
  monthlyCheckInSubmitted: z.boolean(),
  stateChangedAt: z.string(),
});
export type CoachCycleView = z.infer<typeof coachCycleViewSchema>;

// What the doctor sees: the same cycle plus everything that is a clinical
// judgement. Kept as a superset of the coach fields rather than a separate
// shape, so "same underlying data, role-adapted presentation" is visible in
// the types.
export const doctorCycleViewSchema = coachCycleViewSchema.extend({
  state: prescriptionCycleStateSchema,
  // The composed patient-facing ordination label (medication · dose · freq).
  prescription: z.string(),
  medication: z.string().nullable(),
  dose: z.string(),
  frequency: z.string().nullable(),
  prescribedByDoctorId: z.string().nullable(),
  prescribedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  // This month's renewal check-in, once submitted: effect + side effects are
  // the two things that drive the dose decision.
  renewal: z
    .object({
      submittedAt: z.string(),
      currentWeightKg: z.number().nullable(),
      doingWell: z.string().nullable(),
      sideEffects: z.string().nullable(),
      questionsForDoctor: z.string().nullable(),
    })
    .nullable(),
  // Previous cycles, newest first — the dose history the doctor decides
  // against. Label only; the full cycle is one click away.
  previousDecisions: z.array(
    z.object({
      cycleNumber: z.number().int(),
      prescription: z.string(),
      state: prescriptionCycleStateSchema,
      decidedAt: z.string().nullable(),
    }),
  ),
});
export type DoctorCycleView = z.infer<typeof doctorCycleViewSchema>;
